// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { RxDatabase } from 'rxdb';
import { QueryOperator } from '../operators/QueryOperator';
import { MutationOperator } from '../operators/MutationOperator';
import type { QueryOptions } from '../operators/types';
import type { ProjectModel } from '../../models/Project';
import { createId } from '../../index';

export interface ProjectFilters {
  /** Organization ID (optional - omit for cross-org queries in "View All" mode) */
  orgId?: string;
  /** Filter by user ID */
  userId?: string;
  /** Filter by goal ID */
  goalId?: string;
  /** Filter by workspace ID */
  workspaceId?: string;
  /** Filter by codebase ID */
  codebaseId?: string;
  /** Filter by category ID */
  categoryId?: string;
  /** Filter by completion state */
  completed?: boolean;
  /** Filter by archived state (default: false) */
  archived?: boolean;
}

export interface CreateProjectInput {
  /** Project name (kebab-case) */
  name: string;
  /** Project title */
  title: string;
  /** Project description */
  description: string;
  /** Organization ID */
  orgId: string;
  /** User ID of project creator */
  userId: string;
  /** Parent goal ID */
  goalId: string;
  /** Optional workspace ID */
  workspaceId?: string;
  /** Optional codebase ID */
  codebaseId?: string;
  /** Optional category ID */
  categoryId?: string;
  /** Optional tag IDs */
  tagIds?: string[];
  /** Optional planned start date (ISO 8601) */
  startAt?: string;
  /** Optional actual start date (ISO 8601) */
  startedAt?: string;
  /** Optional due date (ISO 8601) */
  dueAt?: string;
  /** Optional completion date (ISO 8601) */
  completedAt?: string;
  /** Optional time budget in hours */
  timeBudgetHours?: number;
  /** Optional MCP and spec tracking metadata */
  metadata?: any;
}

export interface UpdateProjectInput {
  /** Project ID to update */
  id: string;
  /** Partial project data to update */
  updates: Partial<ProjectModel>;
}

export class ProjectOperator {
  constructor(private db: RxDatabase) {}

  query(
    filters: ProjectFilters,
    options?: { sort?: string; limit?: number; skip?: number }
  ): QueryOperator<ProjectModel> {
    const collection = this.db.collections.projects;

    // Build selector - orgId is optional to support "View All" mode across organizations
    // Use $ne: true instead of $eq: false to match documents where archived is false, undefined, or missing
    const selector: any = {
      archived: filters.archived === true ? { $eq: true } : { $ne: true },
    };

    // Only add orgId filter if provided (allows cross-org queries when not specified)
    if (filters.orgId) {
      selector.orgId = { $eq: filters.orgId };
    }

    if (filters.userId) {
      selector.userId = { $eq: filters.userId };
    }

    if (filters.goalId) {
      selector.goalId = { $eq: filters.goalId };
    }

    if (filters.workspaceId) {
      selector.workspaceId = { $eq: filters.workspaceId };
    }

    if (filters.codebaseId) {
      selector.codebaseId = { $eq: filters.codebaseId };
    }

    if (filters.categoryId) {
      selector.categoryId = { $eq: filters.categoryId };
    }

    // Use $ne: true for completed filter to match documents where completed is false, undefined, or missing
    if (filters.completed !== undefined) {
      selector.completed = filters.completed === true ? { $eq: true } : { $ne: true };
    }

    const queryOptions: QueryOptions<ProjectModel> = {
      selector,
      sort: options?.sort ? [{ [options.sort]: 'desc' }] : [{ createdAt: 'desc' }],
      limit: options?.limit,
      skip: options?.skip,
    };

    return new QueryOperator<ProjectModel>(collection, queryOptions);
  }

  async findOne(projectId: string): Promise<ProjectModel | null> {
    const doc = await this.db.collections.projects.findOne(projectId).exec();
    return doc ? (doc.toJSON() as ProjectModel) : null;
  }

  async count(filters: ProjectFilters): Promise<number> {
    const collection = this.db.collections.projects;

    // Build selector - orgId is optional to support "View All" mode
    // Use $ne: true instead of $eq: false to match documents where archived is false, undefined, or missing
    const selector: any = {
      archived: filters.archived === true ? { $eq: true } : { $ne: true },
    };

    if (filters.orgId) {
      selector.orgId = { $eq: filters.orgId };
    }

    if (filters.userId) {
      selector.userId = { $eq: filters.userId };
    }

    if (filters.goalId) {
      selector.goalId = { $eq: filters.goalId };
    }

    if (filters.workspaceId) {
      selector.workspaceId = { $eq: filters.workspaceId };
    }

    if (filters.codebaseId) {
      selector.codebaseId = { $eq: filters.codebaseId };
    }

    if (filters.categoryId) {
      selector.categoryId = { $eq: filters.categoryId };
    }

    // Use $ne: true for completed filter to match documents where completed is false, undefined, or missing
    if (filters.completed !== undefined) {
      selector.completed = filters.completed === true ? { $eq: true } : { $ne: true };
    }

    return await collection.count({ selector }).exec();
  }

  createMutation(): MutationOperator<ProjectModel, CreateProjectInput> {
    return new MutationOperator<ProjectModel, CreateProjectInput>(
      this.db.collections.projects,
      {
        mutationFn: async (collection, input) => {
          const now = new Date().toISOString();
          const project: ProjectModel = {
            id: createId('project'),
            orgId: input.orgId,
            userId: input.userId,
            goalId: input.goalId,
            name: input.name,
            title: input.title,
            description: input.description,
            completed: false,
            archived: false,
            version: 1,
            createdAt: now,
            updatedAt: now,
            ...(input.startAt && { startAt: input.startAt }),
            ...(input.startedAt && { startedAt: input.startedAt }),
            ...(input.dueAt && { dueAt: input.dueAt }),
            ...(input.completedAt && { completedAt: input.completedAt }),
            ...(input.workspaceId && { workspaceId: input.workspaceId }),
            ...(input.codebaseId && { codebaseId: input.codebaseId }),
            ...(input.categoryId && { categoryId: input.categoryId }),
            ...(input.tagIds && { tagIds: input.tagIds }),
            ...(input.timeBudgetHours && { timeBudgetHours: input.timeBudgetHours }),
            ...(input.metadata && { metadata: input.metadata }),
          };

          await collection.insert(project);
          return project;
        },
      }
    );
  }

  updateMutation(): MutationOperator<ProjectModel, UpdateProjectInput> {
    return new MutationOperator<ProjectModel, UpdateProjectInput>(
      this.db.collections.projects,
      {
        mutationFn: async (collection, { id, updates }) => {
          const doc = await collection.findOne(id).exec();
          if (!doc) throw new Error(`Project ${id} not found`);

          await doc.update({
            $set: { ...updates, updatedAt: new Date().toISOString() },
          });

          return doc.toJSON() as ProjectModel;
        },
      }
    );
  }

  deleteMutation(): MutationOperator<void, string> {
    return new MutationOperator<void, string>(this.db.collections.projects, {
      mutationFn: async (collection, projectId) => {
        const doc = await collection.findOne(projectId).exec();
        if (!doc) throw new Error(`Project ${projectId} not found`);

        await doc.update({
          $set: { archived: true, updatedAt: new Date().toISOString() },
        });
      },
    });
  }

  completeMutation(): MutationOperator<ProjectModel, string> {
    return new MutationOperator<ProjectModel, string>(this.db.collections.projects, {
      mutationFn: async (collection, projectId) => {
        const doc = await collection.findOne(projectId).exec();
        if (!doc) throw new Error(`Project ${projectId} not found`);

        await doc.update({
          $set: { completed: true, updatedAt: new Date().toISOString() },
        });

        return doc.toJSON() as ProjectModel;
      },
    });
  }
}
